import { Box, InputAdornment, Popover, Stack, TextField, Tooltip } from "@mui/material";
import { useCallback, useRef } from "react";
import { useLocalStorage } from "@src/appV2/lib/utils/useLocalstorage";
import { Avatar } from "@src/appV2/lib/Avatar";
import { Button } from "@src/appV2/redesign/components/Button";
import { IconButton } from "@src/appV2/redesign/components/IconButton";

import { Icon } from "@src/appV2/redesign/components/Icon";
import { Text } from "@src/appV2/redesign/components/Text";
import { AccountSwitcherMenuWorkplaceDesktop } from "./AccountSwitcherDropdownWorkplace";
import { useAccountSwitcher } from "../../Shared/AccountSwitcher/useAccountSwitcher";
import { AccountSwitcherDropdownAdminDesktop } from "./AccountSwitcherDropdownAdmin";
import {
  type AdminSearchTabValue,
  ADMIN_SEARCH_TAB_STORAGE_KEY,
} from "../../Shared/AccountSwitcher/types";

export function AccountSwitcherDesktop() {
  const anchorRef = useRef<HTMLDivElement>(null);
  const [selectedTab, setSelectedTab] = useLocalStorage<AdminSearchTabValue>(
    ADMIN_SEARCH_TAB_STORAGE_KEY,
    "all"
  );
  const {
    switcherTitle,
    switcherSubtitle,
    displayAdminSwitcher,
    dropdownVisible,
    setDropdownVisible,
    onAdminSearchBarEntered,
    onDropdownButtonClicked,
    onDropdownMouseLeave,
    workerDetails,
    workplaceDetails,
    onSearchChanged,
    onUserClicked,
    onAdminClicked,
    hasSearchTerm,
  } = useAccountSwitcher();

  const handlePopoverClose = useCallback(() => {
    onDropdownMouseLeave();
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  }, [onDropdownMouseLeave]);

  const isAllMode = selectedTab === "all" && hasSearchTerm;

  // For "all" mode with search term: wider dropdown, aligned to left edge of search field
  // For single mode or no search term: same width as search field, aligned to right edge
  const getPopoverWidth = () => {
    if (!displayAdminSwitcher) {
      return anchorRef.current?.offsetWidth;
    }
    return isAllMode ? "700px" : "400px";
  };

  return (
    <Box ref={anchorRef}>
      {displayAdminSwitcher ? (
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          placeholder="Search agents or workplaces..."
          autoComplete="off"
          sx={(theme) => ({
            "& .MuiOutlinedInput-root": {
              backgroundColor: theme.palette.background?.inverted?.muted,
              color: theme.palette.navbar?.itemContentSelected,
              paddingRight: 0,
              "& fieldset": {
                borderColor: theme.palette.button?.outline?.inverted?.normal?.borderColor,
              },
              "&:hover fieldset": {
                borderColor: theme.palette.button?.outline?.inverted?.hover?.borderColor,
              },
              "&.Mui-focused fieldset": {
                borderColor: theme.palette.button?.outline?.inverted?.hover?.borderColor,
              },
            },
            "& .MuiOutlinedInput-input": {
              color: theme.palette.navbar?.itemContentSelected,
              "&::placeholder": {
                color: theme.palette.navbar?.itemContent,
                opacity: 1,
              },
            },
            width: "400px",
          })}
          onFocus={onAdminSearchBarEntered}
          onChange={(event) => {
            setDropdownVisible(true);
            onSearchChanged(event.target.value);
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end" sx={{ marginLeft: 0, paddingLeft: 0 }}>
                <Tooltip title="Switch To Admin">
                  <span>
                    <IconButton
                      variant="linkInverted"
                      iconType="person-male-formal"
                      sx={(theme) => ({
                        borderRadius: 0,
                        borderLeft: `1px solid ${theme.palette.button?.outline?.inverted?.normal?.borderColor}`,
                        paddingX: 3,
                        height: "100%",
                        "&:hover": {
                          backgroundColor: theme.palette.background?.inverted?.muted,
                        },
                      })}
                      onClick={(e) => {
                        e.stopPropagation();
                        onAdminClicked();
                      }}
                    />
                  </span>
                </Tooltip>
              </InputAdornment>
            ),
          }}
        />
      ) : (
        <Button
          variant="linkInverted"
          sx={{ width: "250px", minHeight: "50px" }}
          onClick={onDropdownButtonClicked}
        >
          <Stack direction="row" alignItems="center" gap={3} overflow="hidden" paddingY={1}>
            <Avatar
              name={switcherTitle}
              size="small"
              sx={(theme) => ({
                bgcolor: theme.palette.navbar?.avatarBackground,
                color: theme.palette.navbar?.avatarText,
                flexShrink: 0,
              })}
            />
            <Stack direction="column" flex={1} minWidth={0}>
              <Text
                variant="body1"
                textAlign="left"
                color={(theme) => theme.palette.navbar?.userName}
                noWrap
              >
                {switcherTitle}
              </Text>
              {switcherSubtitle && (
                <Text variant="caption" textAlign="left" noWrap>
                  {switcherSubtitle}
                </Text>
              )}
            </Stack>
            <Icon
              type="caret-bottom"
              sx={(theme) => ({
                color: theme.palette.button?.outline.inverted.hover.color,
                flexShrink: 0,
              })}
            />
          </Stack>
        </Button>
      )}

      <Popover
        open={dropdownVisible}
        anchorEl={anchorRef.current}
        onClose={handlePopoverClose}
        disableAutoFocus
        disableEnforceFocus
        anchorOrigin={{
          vertical: "bottom",
          horizontal: displayAdminSwitcher && isAllMode ? "left" : "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: displayAdminSwitcher && isAllMode ? "left" : "right",
        }}
        slotProps={{
          paper: {
            sx: (theme) => ({
              width: getPopoverWidth(),
              background: theme.palette.navbar?.gradient,
              boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.4)",
              paddingX: displayAdminSwitcher ? 1 : 3,
              paddingY: displayAdminSwitcher ? 0 : 3,
            }),
          },
        }}
      >
        {displayAdminSwitcher ? (
          <AccountSwitcherDropdownAdminDesktop
            workerDetails={workerDetails}
            workplaceDetails={workplaceDetails}
            onUserClicked={onUserClicked}
            hasSearchTerm={hasSearchTerm}
            selectedTab={selectedTab}
            onTabChange={setSelectedTab}
          />
        ) : (
          <AccountSwitcherMenuWorkplaceDesktop
            workplaceDetails={workplaceDetails}
            onUserClicked={onUserClicked}
          />
        )}
      </Popover>
    </Box>
  );
}
